<script lang="ts">
  import { Card, Button, Input, Select, Checkbox, Textarea, Helper } from "flowbite-svelte";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faSave, faTimes, faAsterisk } from "@fortawesome/free-solid-svg-icons";
  import { createEventDispatcher } from "svelte";
  import type { CustomerData, Customer, Lookup, CustomerInput } from "../../services/api/types.js";
  import { getCustomers } from "../../services/api/data-handler.js";

  type ValidFields = "name" | keyof Customer["metadata"];

  interface LookupOption {
    id: string;
    name: string;
  }

  let customerInput: CustomerInput = {
    customer_id: "",
    name: "",
    code: "",
    metadata: {},
    global_lookup_id: "",
  };
  let lookups: Record<string, Lookup>;
  let nameDuplicate = false;
  let nameDebounceTimer: ReturnType<typeof setTimeout>;
  let allCustomers: Customer[] = [];

  export let object: CustomerData;
  export let action: "add" | "edit";

  let initialized = false;
  $: if (object && !initialized) {
    initialized = true;
    customerInput = {
      customer_id: object.customer.customer_id,
      name: object.customer.name,
      code: object.customer.code || "",
      metadata: object.customer.metadata,
      global_lookup_id: object.customer.global_lookup_id,
    };

    const entries = Object.entries(object.lookups);
    const sortedEntries = entries.sort(([, a], [, b]) => a.order - b.order);
    lookups = Object.fromEntries(sortedEntries);

    // Validate all fields immediately
    validateAllFields();
  }

  const dispatch = createEventDispatcher();

  let errors: Record<ValidFields, string> = {
    name: "",
    // code: "",
  };
  $: errorState = errors as Record<string, string>;
  $: hasErrors = Object.values(errorState).some((error) => error !== "");
  $: hasLookups = lookups && Object.keys(lookups).length > 0;

  $: getInputClass = (field: keyof typeof errors) => {
    return errorState[field] ? "error" : "";
  };

  $: if (action === "edit" && customerInput && lookups) {
    validateAllFields();
  }

  function saveChanges() {
    customerInput.name = customerInput.name.trim();
    // customerInput.code = customerInput.code.trim();
    validateAllFields();
    if (Object.values(errorState).every((error) => error === "")) {
      dispatch("save", customerInput);
    }
  }

  function cancelEdit() {
    dispatch("close");
  }

  function validateAllFields() {
    validateField("name", customerInput.name);
    // validateField("code", customerInput.code);

    if (lookups) {
      Object.keys(lookups).forEach((field) => {
        validateFieldSafe(field, customerInput.metadata[field]);
      });
    }
  }

  function validateField(field: ValidFields, value: any) {
    if (field === "name") {
      const trimmedValue = value?.trim();
      if (!trimmedValue) {
        errors[field] = "Name is required";
      } else if (trimmedValue.length < 3) {
        errors[field] = "Name must be at least 3 characters long";
      } else if (nameDuplicate) {
        errors[field] = "Customer name must be unique";
      } else {
        const firstThreeChars = trimmedValue.slice(0, 3).toLowerCase();
        if (firstThreeChars.includes('-')) {
          errors[field] = "The first 3 characters of the name cannot contain a dash (hyphen)";
        } else {
          errors[field] = "";
        }
      }
    // } else if (field === "code") {
    //   if (!value?.trim()) {
    //     errors[field] = "Code is required.";
    //   } else if (value.trim().length < 3) {
    //     errors[field] = "Code must be at least 3 characters long.";
    //   } else {
    //     errors[field] = "";
    //   }
    } else if (field in lookups && field !== "global" && field !== "customer") {
      // Validate lookup fields
      const lookup = lookups[field as keyof typeof lookups] as Lookup;
      if (lookup.type === "multi-select" && (!value || value.length === 0)) {
        errors[field] =
          `At least one ${field.replace(/_/g, " ")} must be selected`;
      } else if (lookup.type === "single-select" && !value?.id) {
        errors[field] = `${field.replace(/_/g, " ")} is required`;
      } else {
        errors[field] = "";
      }
    } else {
      errors[field] = "";
    }
    errorState = { ...errors };
  }

  function validateFieldSafe(field: string, value: any) {
    validateField(field as ValidFields, value);
  }

  function handleCheckboxChange(e: Event, field: string, option: LookupOption) {
    const target = e.target as HTMLInputElement;
    const current = (customerInput.metadata[field] as LookupOption[]) || [];
    const newValue = target.checked
      ? [...current, option]
      : current.filter((item: LookupOption) => item.id !== option.id);

    customerInput.metadata[field] = newValue;
    // Always validate after changing, even if array becomes empty
    validateFieldSafe(field, newValue);
  }

  function isOptionSelected(field: string, optionId: string): boolean {
    const items = customerInput.metadata[field] as LookupOption[] | undefined;
    return items?.some((item: LookupOption) => item.id === optionId) || false;
  }

  async function debouncedNameChange(value: string) {
    clearTimeout(nameDebounceTimer);
    nameDuplicate = false;
    validateField("name", value);
    
    const trimmedValue = value?.trim();
    if (!trimmedValue || trimmedValue.length < 3) {
      return;
    }

    nameDebounceTimer = setTimeout(async () => {
      try {
        if (allCustomers.length === 0) {
          const customersData = await getCustomers();
          allCustomers = customersData.customers || [];
        }

        const isDuplicate = allCustomers.some(
          (customer) =>
            customer.customer_id !== customerInput.customer_id &&
            customer.name.toLowerCase().trim() === trimmedValue.toLowerCase()
        );

        nameDuplicate = isDuplicate;
        validateField("name", trimmedValue);
      } catch (error) {
        logError(error, "CustomerObject", undefined, { message: "Error checking customer name uniqueness" });
      }
    }, 500);
  }

  function handleNameChange(event: Event) {
    const target = event.target as HTMLInputElement;
    customerInput.name = target.value;
    debouncedNameChange(target.value);
  }

  // function handleCustomerCodeKeypress(event: KeyboardEvent) {
  //   // Allow only lowercase letters, numbers, and hyphens
  //   if (!/[a-z0-9-]/.test(event.key)) {
  //     event.preventDefault();
  //   }
  // }

  // function handleCustomerCodeChange(event: Event) {
  //   const target = event.target as HTMLInputElement;
  //   // Keep sanitization as backup for paste events
  //   const sanitizedValue = target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
  //   customerInput.code = sanitizedValue;
  //   target.value = sanitizedValue;
  //   validateField("code", sanitizedValue);
  // }
</script>

<Card class="app-mutation-area max-w-sm">
  <div class="space-y-4">
    <div class="pl-2 pr-2">
      <h4 class="card-label-text">
        Name {#if errorState["name"]}
          <FontAwesomeIcon
            icon={faAsterisk}
            title="Required field"
            class="text-red-400"
          />{/if}
      </h4>
      {#if nameDuplicate}
        <Helper class="helper-text error mb-1">Customer name must be unique</Helper>
      {:else}
        <Helper class="helper-text mb-1">Enter a unique customer name</Helper>
      {/if}
      <Input
        type="text"
        bind:value={customerInput.name}
        data-testid="customer-name-input"
        on:input={handleNameChange}
        class="app-field {getInputClass('name')}"
      />
    </div>
    <div class="overflow-y-auto pl-2 pr-2" style="height: calc(100vh - 18.5rem);">
      <!-- <div>
        <h4 class="card-label-text">
          Code {#if errorState["code"]}
            <FontAwesomeIcon
              icon={faAsterisk}
              title="Required field"
              class="text-red-400"
            />{/if}
        </h4>
        <Helper class="helper-text mb-1"
          >Code is required for locating files in storage. Only lowercase letters, numbers, and hyphens are allowed. Minimum length is 3 characters.</Helper
        >
        <Input
          type="text"
          bind:value={customerInput.code}
          data-testid="customer-code-input"
          on:input={handleCustomerCodeChange}
          on:keypress={handleCustomerCodeKeypress}
          class="app-field {getInputClass('code')}"
          pattern="[a-z0-9-]+"
          title="Only lowercase letters, numbers, and hyphens are allowed"
        />
      </div> -->
      {#if hasLookups}
        {#each Object.entries(lookups) as [field, lookup]}
          {#if lookup?.type}
            <div>
              <h4 class="card-label-text mt-2 mb-1">
                {field
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                {#if errorState[field] && errorState[field] !== ""}
                  <FontAwesomeIcon
                    icon={faAsterisk}
                    title="Required field"
                    class="ml-1 text-red-400"
                  />
                {/if}
              </h4>

              {#if lookup.type === "multi-select"}
                <div class="space-y-1">
                  {#each lookup.list as option}
                    <Checkbox
                      class="card-checkbox"
                      checked={isOptionSelected(field, option.id)}
                      on:change={(e) => handleCheckboxChange(e, field, option)}
                    >
                      {option.name}
                    </Checkbox>
                  {/each}
                </div>
              {:else if lookup.type === "single-select"}
                <div>
                  {#each lookup.list as option}
                    <div class="flex items-center card-radio">
                      <input
                        type="radio"
                        id="{field}-{option.id}"
                        name={field}
                        class="card-radio w-4 h-4 text-gray-600 bg-gray-100 border-gray-300"
                        checked={customerInput.metadata[field]?.id ===
                          option.id}
                        data-testid={`customer-${field}`}
                        on:change={() => {
                          customerInput.metadata[field] = option;
                          validateFieldSafe(field, option);
                        }}
                      />
                      <label
                        for="{field}-{option.id}"
                        class="ml-1 text-sm text-gray-900 dark:text-gray-300"
                        >{option.name}</label
                      >
                    </div>
                  {/each}
                </div>
              {/if}
            </div>
          {/if}
        {/each}
      {/if}
    </div>
  </div>
  <div class="flex items-center justify-end mt-4">
    <Button size="xs" color="primary" class="save-button mr-2" on:click={saveChanges}
      disabled={hasErrors} data-testid="customer-save-button"
    >
      <FontAwesomeIcon icon={faSave} class="mr-2" />Save
    </Button>
    <Button
      size="xs"
      color="light"
      class="cancel-button"
      on:click={cancelEdit}
      data-testid="customer-cancel-button"
    >
      <FontAwesomeIcon icon={faTimes} class="mr-2" />
      Cancel
    </Button>
  </div>
</Card>

<style>
  :global(.card) {
    border-radius: 0;
    box-shadow: none;
    border: 1px solid #e5e7eb;
  }
</style>
